'use client'

import { useSolanaUsdcBalance } from '@/hooks/kalshi/useSolanaBalance'
import usePolygonBalances from '@/hooks/polymarket/usePolygonBalances'
import {
	CHAIN_CONFIG,
	type RelayQuoteResponse,
	type RelayStep,
	getRelayMultiInputQuote,
	getRelayQuote,
	getRelayStatus,
	getUsdcAddress,
} from '@/lib/api/relay'
import { executeWithRelayerFallback } from '@/lib/relayer-fallback'
import { useWallet } from '@/providers/WalletContext'
import { deriveSafeAddress } from '@/utils/safe'
import { useSolanaTransaction } from '@account-kit/react'
import {
	OperationType,
	type RelayClient,
	type SafeTransaction,
} from '@polymarket/builder-relayer-client'
import { PublicKey, TransactionMessage, VersionedTransaction } from '@solana/web3.js'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'

export type WithdrawalStatus =
	| 'idle'
	| 'loading_quote'
	| 'quote_ready'
	| 'executing'
	| 'success'
	| 'error'

export interface WithdrawalQuote {
	steps: RelayStep[]
	totalFeeUsd: string
	estimatedOutput: string
	timeEstimate: number
	inputAmount: string
	raw: RelayQuoteResponse
}

export interface WithdrawalParams {
	amount: string // In USDC units (e.g., "10.50")
	destinationChainId: number
	recipientAddress: string
	useMaxBalance?: boolean // Use exact raw balance instead of parsed amount
}

// Convert relay.link transaction data to SafeTransaction format
function relayTxToSafeTx(data: {
	to?: string
	data?: string
	value?: string
}): SafeTransaction | null {
	if (!(data.to && data.data)) return null
	return {
		to: data.to,
		data: data.data,
		value: data.value || '0',
		operation: OperationType.Call,
	}
}

export function useWithdrawal() {
	const [status, setStatus] = useState<WithdrawalStatus>('idle')
	const [quote, setQuote] = useState<WithdrawalQuote | null>(null)
	const [currentStepIndex, setCurrentStepIndex] = useState(0)
	const [error, setError] = useState<string | null>(null)
	const [txHashes, setTxHashes] = useState<Array<{ chainId: number; hash: string }>>([])

	const queryClient = useQueryClient()
	const { eoaAddress, solanaAddress, walletClient } = useWallet()
	const { connection: solanaConnection, signer: solanaSigner } = useSolanaTransaction()
	const safeAddress = eoaAddress ? deriveSafeAddress(eoaAddress) : undefined

	// Balances
	const { rawUsdcBalance: polygonRawBalance } = usePolygonBalances(safeAddress)
	const { data: solanaBalanceData } = useSolanaUsdcBalance()

	const polygonBalance = useMemo(() => {
		if (!polygonRawBalance) return 0
		return Number(polygonRawBalance) / 1_000_000
	}, [polygonRawBalance])

	const solanaBalance = useMemo(() => {
		if (!solanaBalanceData) return 0
		return solanaBalanceData.balance / 1_000_000
	}, [solanaBalanceData])

	const totalBalance = polygonBalance + solanaBalance

	// Fetch quote
	const fetchQuote = useCallback(
		async (params: WithdrawalParams) => {
			if (!(safeAddress && solanaAddress)) {
				throw new Error('Wallet not connected')
			}

			setStatus('loading_quote')
			setError(null)
			setQuote(null)

			try {
				// Use raw balances when useMaxBalance is true to avoid floating point issues
				const polygonRaw = polygonRawBalance ? BigInt(polygonRawBalance) : BigInt(0)
				const solanaRaw = solanaBalanceData?.balance ? BigInt(solanaBalanceData.balance) : BigInt(0)
				const totalRaw = polygonRaw + solanaRaw

				let amountInSmallestUnit: string
				let amountNum: number

				if (params.useMaxBalance) {
					// Use exact raw total balance
					amountInSmallestUnit = totalRaw.toString()
					amountNum = Number(totalRaw) / 1_000_000
				} else {
					amountNum = Number.parseFloat(params.amount)
					if (Number.isNaN(amountNum) || amountNum <= 0) {
						throw new Error('Invalid amount')
					}
					// Compare using raw values to avoid floating point issues
					const requestedRaw = BigInt(Math.floor(amountNum * 1_000_000))
					if (requestedRaw > totalRaw) {
						throw new Error('Insufficient balance')
					}
					amountInSmallestUnit = requestedRaw.toString()
				}

				const destinationUsdc = getUsdcAddress(params.destinationChainId)

				let response: RelayQuoteResponse

				// Determine routing strategy using raw values
				const needsPolygon = BigInt(amountInSmallestUnit) > solanaRaw
				const needsSolana = BigInt(amountInSmallestUnit) > polygonRaw
				const needsBoth = needsPolygon && needsSolana

				if (needsBoth) {
					// Multi-input: use both chains - use exact raw balances
					const polygonAmount = polygonRaw.toString()
					const solanaAmount = (BigInt(amountInSmallestUnit) - polygonRaw).toString()

					response = await getRelayMultiInputQuote({
						user: safeAddress,
						origins: [
							{
								chainId: CHAIN_CONFIG.polygon.id,
								currency: CHAIN_CONFIG.polygon.usdc,
								amount: polygonAmount,
							},
							{
								chainId: CHAIN_CONFIG.solana.id,
								currency: CHAIN_CONFIG.solana.usdc,
								amount: solanaAmount,
								user: solanaAddress,
							},
						],
						destinationChainId: params.destinationChainId,
						destinationCurrency: destinationUsdc,
						recipient: params.recipientAddress,
						tradeType: 'EXACT_INPUT',
					})
				} else {
					// Single origin - prefer same chain as destination to minimize fees
					const usePolygon =
						params.destinationChainId === CHAIN_CONFIG.polygon.id
							? polygonRaw >= BigInt(amountInSmallestUnit)
							: !needsSolana

					const originChainId = usePolygon ? CHAIN_CONFIG.polygon.id : CHAIN_CONFIG.solana.id
					const originUsdc = getUsdcAddress(originChainId)
					const user = usePolygon ? safeAddress : solanaAddress

					response = await getRelayQuote({
						user,
						originChainId,
						destinationChainId: params.destinationChainId,
						originCurrency: originUsdc,
						destinationCurrency: destinationUsdc,
						amount: amountInSmallestUnit,
						recipient: params.recipientAddress,
						tradeType: 'EXACT_INPUT',
					})
				}

				const gasUsd = Number.parseFloat(response.fees.gas?.amountUsd || '0')
				const relayerUsd = Number.parseFloat(response.fees.relayer?.amountUsd || '0')
				const totalFeeUsd = (gasUsd + relayerUsd).toFixed(2)

				// Use calculated amountNum for display (handles useMaxBalance case)
				const displayAmount = amountNum.toFixed(2)

				const withdrawalQuote: WithdrawalQuote = {
					steps: response.steps,
					totalFeeUsd,
					estimatedOutput: response.details.currencyOut.amountFormatted,
					timeEstimate: response.details.timeEstimate || 5,
					inputAmount: displayAmount,
					raw: response,
				}

				setQuote(withdrawalQuote)
				setStatus('quote_ready')
				return withdrawalQuote
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to get quote'
				setError(message)
				setStatus('error')
				throw err
			}
		},
		[safeAddress, solanaAddress, polygonRawBalance, solanaBalanceData]
	)

	// Execute withdrawal with RelayClient for gas sponsorship
	const execute = useCallback(
		async (relayClient: RelayClient | null) => {
			if (!quote) {
				setError('No quote available')
				setStatus('error')
				return
			}

			setStatus('executing')
			setCurrentStepIndex(0)
			setTxHashes([])

			try {
				const hashes: Array<{ chainId: number; hash: string }> = []

				// Group transactions by chain
				const polygonTxs: SafeTransaction[] = []
				const solanaTxs: Array<{ instructions: unknown[] }> = []

				for (const step of quote.steps) {
					for (const item of step.items) {
						if (item.data?.chainId === CHAIN_CONFIG.polygon.id) {
							const safeTx = relayTxToSafeTx(item.data)
							if (safeTx) polygonTxs.push(safeTx)
						} else if (item.data?.instructions) {
							solanaTxs.push({ instructions: item.data.instructions })
						}
					}
				}

				// Execute Polygon transactions via RelayClient (batched, gas sponsored)
				// Uses fallback relayer automatically on 429
				if (polygonTxs.length > 0) {
					if (!relayClient) {
						throw new Error('Trading session not initialized. Please set up your wallet first.')
					}

					setCurrentStepIndex(0)

					const response = await executeWithRelayerFallback(
						relayClient,
						walletClient,
						polygonTxs,
						'Withdraw USDC via Relay'
					)
					const result = (await response.wait()) as { txHash?: string } | null

					if (result?.txHash) {
						hashes.push({ chainId: CHAIN_CONFIG.polygon.id, hash: result.txHash })
						setTxHashes([...hashes])
					}
				}

				// Execute Solana transactions
				if (solanaTxs.length > 0) {
					if (!(solanaConnection && solanaSigner && solanaAddress)) {
						throw new Error('Solana wallet not available')
					}

					setCurrentStepIndex(polygonTxs.length > 0 ? 1 : 0)

					// For Solana, we need to build and sign the transaction
					// The relay API returns instructions that need to be compiled
					for (const solanaTx of solanaTxs) {
						const { blockhash } = await solanaConnection.getLatestBlockhash()

						// Parse instructions from relay response
						// Data is hex-encoded (e.g., "0b9c60da27a3...")
						const instructions = (
							solanaTx.instructions as Array<{
								keys: Array<{ pubkey: string; isSigner: boolean; isWritable: boolean }>
								programId: string
								data: string
							}>
						).map(ix => ({
							keys: ix.keys.map(k => ({
								pubkey: new PublicKey(k.pubkey),
								isSigner: k.isSigner,
								isWritable: k.isWritable,
							})),
							programId: new PublicKey(ix.programId),
							data: Buffer.from(ix.data, 'hex'),
						}))

						const message = new TransactionMessage({
							payerKey: new PublicKey(solanaAddress),
							recentBlockhash: blockhash,
							instructions,
						}).compileToV0Message()

						const tx = new VersionedTransaction(message)

						// Sign with user's wallet
						const signedTx = await solanaSigner.addSignature(tx)

						// Broadcast
						let signature: string
						try {
							signature = await solanaConnection.sendRawTransaction(signedTx.serialize(), {
								skipPreflight: false,
								preflightCommitment: 'confirmed',
							})
						} catch (err) {
							const errorMessage = err instanceof Error ? err.message : String(err)
							if (
								errorMessage.includes(
									'Attempt to debit an account but found no record of a prior credit'
								)
							) {
								throw new Error(
									"We're having issues landing the transaction on Solana. The chain might be busy, please retry."
								)
							}
							throw err
						}
						for (let i = 0; i < 30; i++) {
							const txStatus = await solanaConnection.getSignatureStatus(signature)
							if (
								txStatus.value?.confirmationStatus === 'confirmed' ||
								txStatus.value?.confirmationStatus === 'finalized'
							) {
								break
							}
							if (txStatus.value?.err) {
								throw new Error(`Transaction failed: ${JSON.stringify(txStatus.value.err)}`)
							}
							await new Promise(r => setTimeout(r, 1000))
						}

						hashes.push({ chainId: CHAIN_CONFIG.solana.id, hash: signature })
						setTxHashes([...hashes])
					}
				}

				// Poll for relay completion
				for (const step of quote.steps) {
					if (step.requestId) {
						let attempts = 0
						while (attempts < 60) {
							try {
								const statusResult = await getRelayStatus(step.requestId)
								if (statusResult.status === 'success') {
									break
								}
								if (statusResult.status === 'failure') {
									console.warn('[Withdrawal] Relay failed:', statusResult.data?.failReason)
									break
								}
							} catch {
								// Status endpoint might not be available yet
							}
							await new Promise(r => setTimeout(r, 2000))
							attempts++
						}
					}
				}

				setTxHashes(hashes)
				setStatus('success')

				// Invalidate balance queries
				queryClient.invalidateQueries({ queryKey: ['usdcBalance'] })
				queryClient.invalidateQueries({ queryKey: ['solanaBalance'] })
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Withdrawal failed'
				setError(message)
				setStatus('error')
				console.error('[Withdrawal] Error:', err)
			}
		},
		[quote, solanaConnection, solanaSigner, solanaAddress, queryClient, walletClient]
	)

	const reset = useCallback(() => {
		setStatus('idle')
		setQuote(null)
		setCurrentStepIndex(0)
		setError(null)
		setTxHashes([])
	}, [])

	return {
		// Balances
		polygonBalance,
		solanaBalance,
		totalBalance,
		// Quote
		quote,
		fetchQuote,
		// Execution
		execute,
		// Status
		status,
		currentStepIndex,
		error,
		txHashes,
		// Actions
		reset,
	}
}
